import { Box } from '@factorialco/deprecated-design-system'
import { DataSchemaItemFragment } from 'generated/resources/graphql'

import NumberField from 'modules/core/components/NumberField'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import {
  CENTS,
  MAX_PERCENTAGE_WORKING_HOURS,
  MIN_PERCENTAGE_WORKING_HOURS,
  constraintToWorkingHoursTimePercentage,
  getStringFieldValue,
} from 'modules/employees/components/UnifiedAddEmployeeModal/utils'
import { WorkingConditions } from 'modules/job_catalog/lib/workingConditions'

import MatchingValueTooltip from './MatchingValueTooltip'
import { WorkingConditionsFields, useWorkingConditionsInitialValue } from './workingConditions'

interface Props {
  fieldName: keyof WizardData
  field: DataSchemaItemFragment
  data: WizardData
  onChangeField: (fieldName: string, value: string) => void
  getFieldError?: (fieldName: keyof WizardData) => string | undefined
  workingConditions?: WorkingConditions
}

export const IntegerField = ({
  fieldName,
  field,
  data,
  onChangeField,
  getFieldError,
  workingConditions,
}: Props) => {
  const isWorkingConditionField = WorkingConditionsFields.includes(field.fieldName)

  const initialValue = useWorkingConditionsInitialValue(
    field,
    fieldName,
    data,
    onChangeField,
    workingConditions
  )
  if (field.format == 'percentage_in_cents') {
    return (
      <NumberField
        label={field.label}
        value={data[fieldName] ? +data[fieldName] / CENTS : undefined}
        onChange={(value: number | null) => {
          onChangeField(
            fieldName,
            value !== null ? `${constraintToWorkingHoursTimePercentage(value) * CENTS}` : ''
          )
        }}
        placeholder={i18n.t(
          'add_employee_modal.contract_information_step.fields.percentage_in_cents.placeholder'
        )}
        step={1}
        min={MIN_PERCENTAGE_WORKING_HOURS}
        max={MAX_PERCENTAGE_WORKING_HOURS}
        indicator="%"
        maxDecimals={2}
      />
    )
  }

  const convertCentsValue = (value: string) => {
    if (value === '') return undefined
    if (field.format != 'cents') return value

    // Use parseFloat for working hours fields to preserve decimals
    const parseFunction =
      field.fieldName === 'contracts_max_legal_yearly_hours' ||
      field.fieldName === 'contracts_maximum_weekly_hours'
        ? parseFloat
        : parseInt

    return String(parseFunction(value) * 100)
  }

  const convertCentsToUI = (value: string) => {
    if (field.format != 'cents') return value
    // Use parseFloat for working hours fields to preserve decimals
    const parseFunction =
      field.fieldName === 'contracts_max_legal_yearly_hours' ||
      field.fieldName === 'contracts_maximum_weekly_hours'
        ? parseFloat
        : parseInt

    return String(parseFunction(value) / 100)
  }

  const renderSimpleField = () => (
    <TextField
      key={field.fieldName}
      label={field.label}
      value={convertCentsToUI(getStringFieldValue(fieldName, data))}
      onChange={(value) => onChangeField(fieldName, convertCentsValue(value) || '')}
      type="number"
      min={0}
      error={!!getFieldError ? getFieldError(fieldName) : undefined}
      mandatory={field.required}
    />
  )

  // Field is rendered inside a Box only when it's linked to a working condition
  // and the working condition has a value to check the field against.
  if (!isWorkingConditionField || !initialValue) return renderSimpleField()

  //isWorkingConditionField && initialValue
  // -> render field within a Box to place the MatchingValueTooltip properly
  return (
    <Box position="relative">
      {renderSimpleField()}
      <Box position="absolute" style={{ right: '38px', top: '34px' }}>
        <MatchingValueTooltip
          type="workingCondition"
          linked={data[fieldName] === initialValue}
          onClick={() => onChangeField(fieldName, initialValue)}
          workingConditionName={workingConditions?.name}
        />
      </Box>
    </Box>
  )
}
